import React, { useCallback, useEffect, useRef, useState } from "react";
import query from "jquery";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { refreshCartPageWithSaveProductCart } from "../redux/actions/action-creator";
import GuestLoginRequiredModal from "./GuestLoginRequiredModal";
import MobileNavigation from "./MobileNavigation";
import ProductImage from "./common/ProductImage";
import { isGuestUser } from "../utils/guestLogin";
import { getGuestLocationDisplayText, getMarketMetadata, openDeliveryLocation } from "../utils/locationDefaults";
import { getSegmentPath, isSeparateServiceSegment } from "../utils/segmentFlow";
import { hasCartId } from "../utils/segmentCart";
import { OPEN_SERVICE_SEARCH_EVENT, SERVICE_SEARCH_PLACEHOLDER } from "../utils/serviceSearch";
import {
  API_ENV_STORAGE_KEY,
  setApiEnvironment,
} from "../axios/axiosinstance";
import "./header.css";
import "./header-responsive.css";

const HEADER_THEME_STORAGE_KEY = "rghtnow_header_theme";
const SHOW_API_ENV_TOGGLE = process.env.REACT_APP_SHOW_ENV_TOGGLE === "true";

const headerThemeOptions = [
  { value: "current", label: "Current Color", swatchClassName: "rn-header-theme-swatch--current" },
  { value: "white", label: "White Color", swatchClassName: "rn-header-theme-swatch--white" },
];

const getInitialHeaderTheme = () => {
  if (typeof window === "undefined") {
    return "current";
  }

  return localStorage.getItem(HEADER_THEME_STORAGE_KEY) === "white"
    ? "white"
    : "current";
};

const getInitialApiEnvironment = () => {
  if (typeof window === "undefined") return "dev";

  return localStorage.getItem(API_ENV_STORAGE_KEY) === "live" ? "live" : "dev";
};

const getCellContents = (data) => {
  const candidates = [
    data?.cell_contents,
    data?.response_data?.[0]?.cell_contents,
    data?.response_data,
    data?.data?.cell_contents,
    data?.data?.response_data?.[0]?.cell_contents,
    data?.data?.response_data,
    data?.data,
    data,
  ];

  return candidates.find((item) => Array.isArray(item)) || [];
};

const getSegmentId = (item) => item?.segment_id || item?.segmentId || item?.id;

const getSegmentName = (item) => {
  const name = item?.name || item?.title || item?.segment_name || item?.segmentName || "Service";

  return String(name).trim().toLowerCase() === "saloon" ? "Salon" : name;
};

const getSegmentImage = (item) =>
  item?.image || item?.icon || item?.logo || item?.segment_image || item?.segmentImage || "";

const isComingSoonSegment = (item) =>
  isSeparateServiceSegment(item) ||
  item?.is_coming_soon === true ||
  Number(item?.is_coming_soon || item?.isComingSoon || item?.coming_soon) === 1;

const getSearchPlaceholder = (pathname) => {
  if (pathname === "/home" || pathname === "/") return SERVICE_SEARCH_PLACEHOLDER;
  if (pathname === "/stores" || pathname.startsWith("/stores/")) return "Search for store/product";
  if (
    pathname === "/shop" ||
    pathname.startsWith("/shop/") ||
    pathname.startsWith("/category/") ||
    pathname.startsWith("/store-category")
  ) {
    return "Search for product";
  }

  return "Search products, stores or services";
};

const BasketBadge = ({ count }) => count > 0 ? (
  <span className="rn-basket-badge" aria-hidden="true">{count}</span>
) : null;

const Header = ({ category, mobileHomeHeader = false, mobileHeaderTitle = "Home", className = "" }) => {
  const [scroll, setScroll] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const middleTopRef = useRef(null);
  const accountMenuRef = useRef(null);
  const topAccountMenuRef = useRef(null);
  const serviceSearchPopupRef = useRef(null);
  const { userData = null, address, user_location, country_id } = useSelector((state) => state.auth);
  const servicesData = useSelector((state) => state.common?.servicesData);
  const cartState = useSelector((state) => state.cart);
  const cartUpdating = useSelector((state) => Boolean(state.cart?.saveCartLoading || state.cart?.getCartLoading));
  const basketOpeningRef = useRef(false);
  const cartItems = useSelector(
    (state) =>
      state.cart?.cartItems ||
      state.cart?.data?.product_details ||
      []
  );
  const shoppingCartAvailable = cartItems.length > 0 || hasCartId(cartState?.cartId) ||
    hasCartId(cartState?.serverCartSummary?.cart_id);
  const serviceCartAvailable = hasCartId(cartState?.serviceCartSummary?.cart_id);
  const shoppingBadgeAvailable = cartState?.serverCartSummary
    ? cartState.serverCartSummary.product_count > 0
    : Array.isArray(cartItems) && cartItems.length > 0;
  const serviceBadgeAvailable = cartState?.serviceCartSummary?.product_count > 0;
  const basketCount = Number(shoppingBadgeAvailable) + Number(serviceBadgeAvailable);
  const basketTitle = basketCount > 0 ? `${basketCount} active ${basketCount === 1 ? "cart" : "carts"}` : "Basket empty";

  const [emptyCartOpen, setEmptyCartOpen] = useState(false);
  const [basketLoading, setBasketLoading] = useState(false);
  const [guestLoginModalOpen, setGuestLoginModalOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [topAccountMenuOpen, setTopAccountMenuOpen] = useState(false);
  const [headerTheme, setHeaderTheme] = useState(getInitialHeaderTheme);
  const [apiEnvironment, setApiEnvironmentState] = useState(getInitialApiEnvironment);
  const isLoggedIn = Boolean(userData?.id || localStorage.getItem("access_token"));
  const guestUser = isGuestUser(userData);
  const mobileLocationText = guestUser
    ? getGuestLocationDisplayText(
        { ...(user_location || {}), address: address || user_location?.address },
        "Select your delivery location"
      )
    : address ||
      user_location?.address ||
      "Select your delivery location";
  const activeMarket = getMarketMetadata({
    ...(user_location || {}),
    address: address || user_location?.address,
    country_id: country_id ?? user_location?.country_id ?? user_location?.countryId,
  });
  const pathname = location.pathname || "/";
  const searchPlaceholder = getSearchPlaceholder(pathname);
  const isHomeRoute = pathname === "/home" || pathname === "/";
  const hideHeaderSearch = ["/cart", "/address-list", "/my-account", "/orders"].includes(
    pathname.replace(/\/+$/, "")
  );
  const serviceSearchItems = getCellContents(servicesData);

  const showGuestLoginModal = (event) => {
    event?.preventDefault();
    setEmptyCartOpen(false);
    setGuestLoginModalOpen(true);
  };

  const handleGuestLoginContinue = () => {
    setGuestLoginModalOpen(false);
    navigate("/login");
  };

  const handleLogout = () => {
    const selectedEnvironment = localStorage.getItem(API_ENV_STORAGE_KEY);
    localStorage.clear();
    if (selectedEnvironment) localStorage.setItem(API_ENV_STORAGE_KEY, selectedEnvironment);
    localStorage.setItem("forceLogout", String(Date.now()));
    navigate("/login");
  };

  const handleHeaderThemeChange = (theme) => {
    setHeaderTheme(theme);
    localStorage.setItem(HEADER_THEME_STORAGE_KEY, theme);
  };

  const handleApiEnvironmentChange = () => {
    const nextEnvironment = apiEnvironment === "dev" ? "live" : "dev";
    const nextEnvironmentLabel = nextEnvironment === "live" ? "Live" : "Dev";
    const shouldContinue = window.confirm(
      `Switching to ${nextEnvironmentLabel} will clear your current local data. Your data will be loaded again from the ${nextEnvironmentLabel} API. Do you want to continue?`
    );

    if (!shouldContinue) return;

    localStorage.clear();
    setApiEnvironment(nextEnvironment);
    setApiEnvironmentState(nextEnvironment);
    window.location.reload();
  };

  const handleBasketClick = async (event) => {
    if (guestUser) {
      showGuestLoginModal(event);
      return;
    }

    event.preventDefault();
    if (!shoppingCartAvailable && serviceCartAvailable) {
      setComingSoonService(cartState.serviceCartSummary);
      return;
    }
    if (basketOpeningRef.current || cartUpdating) return;

    basketOpeningRef.current = true;
    setBasketLoading(true);
    setEmptyCartOpen(false);
    try {
      const hasCartItems = await dispatch(refreshCartPageWithSaveProductCart());
      if (hasCartItems) {
        navigate("/cart");
      } else {
        setEmptyCartOpen(true);
      }
    } finally {
      basketOpeningRef.current = false;
      setBasketLoading(false);
    }
  };

  const handleOrdersClick = (event) => {
    if (guestUser) {
      showGuestLoginModal(event);
    }
  };

  const handleProfileClick = (event) => {
    if (guestUser) {
      showGuestLoginModal(event);
      return;
    }
  };

  const handleUserAccountClick = (event) => {
    if (guestUser) {
      showGuestLoginModal(event);
      return;
    }
    event.preventDefault();
    setTopAccountMenuOpen(false);
    setAccountMenuOpen((isOpen) => !isOpen);
  };

  const handleAddressClick = (event) => {
    event.preventDefault();
    openDeliveryLocation();
  };

  const handleTopAccountClick = (event) => {
    if (guestUser) {
      showGuestLoginModal(event);
      return;
    }
    event.preventDefault();
    setAccountMenuOpen(false);
    setTopAccountMenuOpen((isOpen) => !isOpen);
  };

  useEffect(() => {
    const updateHeaderStickiness = () => {
      const isDesktop = window.matchMedia("(min-width: 992px)").matches;
      const middleTopHeight = middleTopRef.current?.offsetHeight || 0;

      setScroll(isDesktop && middleTopHeight > 0 && window.pageYOffset >= middleTopHeight);
    };

    updateHeaderStickiness();
    window.addEventListener("scroll", updateHeaderStickiness, { passive: true });
    window.addEventListener("resize", updateHeaderStickiness);

    const selectElement = query(".js-example-basic-single");
    selectElement.select2();

    return () => {
      window.removeEventListener("scroll", updateHeaderStickiness);
      window.removeEventListener("resize", updateHeaderStickiness);
      if (selectElement.data("select2")) {
        selectElement.select2("destroy");
      }
    };
  }, []);

  useEffect(() => {
    const handleOutsideAccountClick = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setAccountMenuOpen(false);
      }
      if (topAccountMenuRef.current && !topAccountMenuRef.current.contains(event.target)) {
        setTopAccountMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideAccountClick);
    return () => document.removeEventListener("mousedown", handleOutsideAccountClick);
  }, []);

  useEffect(() => {
    if (!emptyCartOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setEmptyCartOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [emptyCartOpen]);

  // Set the default language
  const [selectedLanguage, setSelectedLanguage] = useState("Eng");
  const handleLanguageChange = (language) => {
    setSelectedLanguage(language);
  };

  // Set the default currency
  const [selectedCurrency, setSelectedCurrency] = useState(activeMarket.currency);
  const handleCurrencyChange = (currency) => {
    setSelectedCurrency(currency);
  };

  useEffect(() => {
    setSelectedCurrency(activeMarket.currency);
  }, [activeMarket.currency]);

  // Mobile menu support
  const [menuActive, setMenuActive] = useState(false);
  const closeMobileMenu = useCallback(() => setMenuActive(false), []);
  const handleMenuToggle = () => {
    setMenuActive(!menuActive);
  };

  // Search control support
  const [activeSearch, setActiveSearch] = useState(false);
  const [serviceSearchOpen, setServiceSearchOpen] = useState(false);
  const [serviceSearchTerm, setServiceSearchTerm] = useState("");
  const [comingSoonService, setComingSoonService] = useState(null);
  const normalizedServiceSearchTerm = serviceSearchTerm.trim().toLowerCase();
  const filteredServiceSearchItems = normalizedServiceSearchTerm
    ? serviceSearchItems.filter((item) =>
        String(getSegmentName(item)).toLowerCase().includes(normalizedServiceSearchTerm)
      )
    : serviceSearchItems;
  const handleSearchToggle = () => {
    setActiveSearch(!activeSearch);
  };
  const openServiceSearch = () => {
    if (isHomeRoute) {
      setServiceSearchOpen(true);
    }
  };
  const closeServiceSearch = () => {
    setServiceSearchOpen(false);
    setServiceSearchTerm("");
  };
  const closeComingSoonService = () => {
    setComingSoonService(null);
  };
  const handleSearchSubmit = (event) => {
    event.preventDefault();
    openServiceSearch();
  };
  const handleSearchFocus = () => {
    openServiceSearch();
  };
  const handleSegmentSearchSelect = (item) => {
    const segmentId = getSegmentId(item);

    if (isComingSoonSegment(item)) {
      closeServiceSearch();
      setActiveSearch(false);
      setComingSoonService(item);
      return;
    }

    if (!segmentId) return;

    closeServiceSearch();
    setActiveSearch(false);
    navigate(getSegmentPath(item), { state: { segment: item } });
  };
  const searchInputProps = {
    placeholder: searchPlaceholder,
    "aria-label": searchPlaceholder,
    onFocus: handleSearchFocus,
    onClick: handleSearchFocus,
    readOnly: isHomeRoute,
  };

  useEffect(() => {
    closeServiceSearch();
    setActiveSearch(false);
    setMenuActive(false);
  }, [pathname]);

  useEffect(() => {
    if (!isHomeRoute) return undefined;

    const handleOpenServiceSearch = () => {
      setActiveSearch(false);
      setServiceSearchOpen(true);
    };

    window.addEventListener(OPEN_SERVICE_SEARCH_EVENT, handleOpenServiceSearch);
    return () => window.removeEventListener(OPEN_SERVICE_SEARCH_EVENT, handleOpenServiceSearch);
  }, [isHomeRoute]);

  useEffect(() => {
    if (!serviceSearchOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (
        serviceSearchPopupRef.current &&
        !serviceSearchPopupRef.current.contains(event.target)
      ) {
        closeServiceSearch();
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeServiceSearch();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [serviceSearchOpen]);

  useEffect(() => {
    if (!comingSoonService) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeComingSoonService();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [comingSoonService]);

  const headerClassName = [
    "header-middle style-two rghtnow-app-header",
    scroll ? "rghtnow-fixed-header" : "",
    headerTheme === "white"
      ? "rn-header-theme-white"
      : "bg-color-primary rn-header-theme-current",
    mobileHomeHeader ? "rghtnow-home-mobile-header" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <div className='overlay' />
      {/* ==================== Search Box Start Here ==================== */}

      {!hideHeaderSearch && (
      <form action='#' className={`search-box ${activeSearch && "active"}`} onSubmit={handleSearchSubmit}>
        <button
          onClick={handleSearchToggle}
          type='button'
          className='search-box__close position-absolute inset-block-start-0 inset-inline-end-0 m-16 w-48 h-48 border border-gray-100 rounded-circle flex-center text-white hover-text-gray-800 hover-bg-white text-2xl transition-1'
          aria-label='Close search'
        >
          <i className='ph ph-x' />
        </button>
        <div className='container'>
          <div className='position-relative'>
            <label className='visually-hidden' htmlFor='global-search-input'>
              {searchPlaceholder}
            </label>
            <input
              id='global-search-input'
              type='text'
              className='form-control py-16 px-24 text-xl rounded-pill pe-64'
              {...searchInputProps}
            />
            <button
              type='submit'
              className='w-48 h-48 bg-main-600 rounded-circle flex-center text-xl text-white position-absolute top-50 translate-middle-y inset-inline-end-0 me-8'
              aria-label='Submit search'
            >
              <i className='ph ph-magnifying-glass' />
            </button>
          </div>
        </div>
      </form>
      )}
      {/* ==================== Search Box End Here ==================== */}
      {serviceSearchOpen && isHomeRoute && !hideHeaderSearch && (
        <div id='service-search-dialog' className='rn-service-search-modal' role='dialog' aria-modal='true' aria-labelledby='service-search-title'>
          <div className='rn-service-search-panel' ref={serviceSearchPopupRef}>
            <div className='rn-service-search-head'>
              <div>
                <span>Services</span>
                <h3 id='service-search-title'>{SERVICE_SEARCH_PLACEHOLDER}</h3>
              </div>
              <button
                type='button'
                className='rn-service-search-close'
                onClick={closeServiceSearch}
                aria-label='Close services search'
              >
                <i className='ph ph-x' aria-hidden='true' />
              </button>
            </div>
            <div className='rn-service-search-input-wrap'>
              <i className='ph ph-magnifying-glass' aria-hidden='true' />
              <input
                type='search'
                value={serviceSearchTerm}
                onChange={(event) => setServiceSearchTerm(event.target.value)}
                placeholder={SERVICE_SEARCH_PLACEHOLDER}
                aria-label={SERVICE_SEARCH_PLACEHOLDER}
                autoFocus
              />
            </div>
            <div className='rn-service-search-list'>
              {filteredServiceSearchItems.length === 0 && (
                <div className='rn-service-search-empty'>No services found.</div>
              )}
              {filteredServiceSearchItems.map((item, index) => {
                const segmentId = getSegmentId(item);
                const segmentName = getSegmentName(item);
                const isComingSoon = isComingSoonSegment(item);

                return (
                  <button
                    type='button'
                    className={`rn-service-search-item ${isComingSoon ? "is-coming-soon" : ""}`}
                    key={`service-search-${segmentId || index}`}
                    onClick={() => handleSegmentSearchSelect(item)}
                  >
                    <span className='rn-service-search-image'>
                      {isComingSoon && (
                        <span className='rn-service-search-coming-soon'>Coming Soon</span>
                      )}
                      <ProductImage src={getSegmentImage(item)} alt={segmentName} />
                    </span>
                    <span className='rn-service-search-name'>{segmentName}</span>
                    <i className='ph ph-caret-right rn-service-search-arrow' aria-hidden='true' />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {comingSoonService && (
        <div
          className='service-coming-soon-overlay rn-service-coming-soon-overlay'
          role='presentation'
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeComingSoonService();
            }
          }}
        >
          <div
            className='service-coming-soon-panel'
            role='dialog'
            aria-modal='true'
            aria-labelledby='header-service-coming-soon-title'
          >
            <button
              type='button'
              className='service-coming-soon-close'
              onClick={closeComingSoonService}
              aria-label='Close'
            >
              <i className='ph ph-x' aria-hidden='true' />
            </button>
            <span className='service-coming-soon-handle' aria-hidden='true' />
            <div className='service-coming-soon-image'>
              <ProductImage src={getSegmentImage(comingSoonService)} alt={getSegmentName(comingSoonService)} />
            </div>
            <h3 id='header-service-coming-soon-title'>
              {getSegmentName(comingSoonService)} is coming soon!
            </h3>
            <p>We're working hard to bring this service to your area. Stay tuned!</p>
            <button
              type='button'
              className='service-coming-soon-primary'
              onClick={closeComingSoonService}
            >
              <i className='ph ph-bell-ringing' aria-hidden='true' />
              <span>Notify me - It's on</span>
            </button>
            <button
              type='button'
              className='service-coming-soon-secondary'
              onClick={closeComingSoonService}
            >
              Explore available services
            </button>
          </div>
        </div>
      )}
      {/* ==================== Mobile Menu Start Here ==================== */}
      <MobileNavigation open={menuActive} onClose={closeMobileMenu} guestUser={guestUser}
        onLoginRequired={showGuestLoginModal} marketLabel={activeMarket.marketLabel} currency={selectedCurrency} />
      {/* ==================== Mobile Menu End Here ==================== */}
      {/* ======================= Middle Top Start ========================= */}
      <div ref={middleTopRef} className='header-top flex-between rghtnow-middle-top'>
        <div className='container container-lg'>
          <div className='flex-between flex-wrap gap-8'>
            <ul className='flex-align flex-wrap d-none d-md-flex'>
              <li className='border-right-item'>
                <Link
                  to='/onboard-your-store'
                  className='text-white text-sm hover-text-decoration-underline'
                >
                  Become a Seller
                </Link>
              </li>
              <li className='border-right-item'>
                <Link
                  to='/about-page'
                  className='text-white text-sm hover-text-decoration-underline'
                >
                  About us
                </Link>
              </li>
              <li className='border-right-item'>
                <Link
                  to='/free-delivery'
                  className='text-white text-sm hover-text-decoration-underline'
                >
                  Free Delivery
                </Link>
              </li>
              <li className='border-right-item'>
                <Link
                  to='/returns-policy'
                  className='text-white text-sm hover-text-decoration-underline'
                >
                  Returns Policy
                </Link>
              </li>
            </ul>
            <ul className='header-top__right flex-align flex-wrap'>
              <li className='border-right-item border-right-item-sm-space'>
                <span className='text-white text-sm py-8 d-inline-flex'>
                  {activeMarket.marketLabel} • {selectedCurrency}
                </span>
              </li>
              <li className='on-hover-item border-right-item border-right-item-sm-space has-submenu arrow-white'>
                <Link to='/help-center' className='text-white text-sm py-8'>
                  Help Center
                </Link>
                <ul className='on-hover-dropdown common-dropdown common-dropdown--sm max-h-200 scroll-sm px-0 py-8'>
                  <li className='nav-submenu__item'>
                    <Link
                      to='/help-center'
                      className='nav-submenu__link hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                    >
                      <span className='text-sm d-flex'>
                        <i className='ph ph-headset' />
                      </span>
                      Call Center
                    </Link>
                  </li>
                  <li className='nav-submenu__item'>
                    <Link
                      to='/contact-us'
                      className='nav-submenu__link hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                    >
                      <span className='text-sm d-flex'>
                        <i className='ph ph-chat-circle-dots' />
                      </span>
                      Live Chat
                    </Link>
                  </li>
                </ul>
              </li>
              <li className='on-hover-item border-right-item border-right-item-sm-space has-submenu arrow-white'>
                {/* Display the selected language here */}
                <Link to='#' className='selected-text text-white text-sm py-8'>
                  {selectedLanguage}
                </Link>
                <ul className='selectable-text-list on-hover-dropdown common-dropdown common-dropdown--sm max-h-200 scroll-sm px-0 py-8'>
                  <li>
                    <Link
                      to='#'
                      className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                      onClick={() => handleLanguageChange("English")}
                    >
                      <img
                        src='assets/images/thumbs/flag1.png'
                        alt='English'
                        className='w-16 h-12 rounded-4 border border-gray-100'
                      />
                      English
                    </Link>
                  </li>
                  {/*
                  <li>
                    <Link
                      to='#'
                      className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                      onClick={() => handleLanguageChange("Japan")}
                    >
                      <img
                        src='assets/images/thumbs/flag2.png'
                        alt=''
                        className='w-16 h-12 rounded-4 border border-gray-100'
                      />
                      Japan
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='#'
                      className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                      onClick={() => handleLanguageChange("French")}
                    >
                      <img
                        src='assets/images/thumbs/flag3.png'
                        alt=''
                        className='w-16 h-12 rounded-4 border border-gray-100'
                      />
                      French
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='#'
                      className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                      onClick={() => handleLanguageChange("Germany")}
                    >
                      <img
                        src='assets/images/thumbs/flag4.png'
                        alt=''
                        className='w-16 h-12 rounded-4 border border-gray-100'
                      />
                      Germany
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='#'
                      className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                      onClick={() => handleLanguageChange("Bangladesh")}
                    >
                      <img
                        src='assets/images/thumbs/flag6.png'
                        alt=''
                        className='w-16 h-12 rounded-4 border border-gray-100'
                      />
                      Bangladesh
                    </Link>
                  </li>
                  <li>
                    <Link
                      to='#'
                      className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                      onClick={() => handleLanguageChange("South Korea")}
                    >
                      <img
                        src='assets/images/thumbs/flag5.png'
                        alt=''
                        className='w-16 h-12 rounded-4 border border-gray-100'
                      />
                      South Korea
                    </Link>
                  </li>
                  */}
                </ul>
              </li>
              <li ref={topAccountMenuRef} className='border-right-item position-relative'>
                {isLoggedIn ? (
                  <>
                    <button
                      type='button'
                      className='text-white text-sm py-8 flex-align gap-6 bg-transparent border-0'
                      onClick={handleTopAccountClick}
                      aria-expanded={topAccountMenuOpen}
                      aria-label='Open My Account menu'
                    >
                      <span className='icon text-md d-flex'><i className='ph ph-user-circle' /></span>
                      <span className='hover-text-decoration-underline'>My Account</span>
                    </button>
                    {topAccountMenuOpen && (
                      <div className='bg-white shadow-sm border border-gray-100 rounded-8 py-8 position-absolute end-0' style={{ minWidth: "230px", top: "calc(100% + 10px)", zIndex: 99 }}>
                        <div className='border-bottom border-gray-100 px-16 py-8 mb-4'>
                          <div className='text-gray-900 text-sm fw-semibold'>My Account</div>
                        </div>
                        <Link to='/my-account' className='text-gray-700 text-sm py-8 px-16 flex-align gap-8 hover-bg-gray-100' onClick={() => setTopAccountMenuOpen(false)}>
                          <i className='ph ph-user' /> Profile
                        </Link>
                        <button type='button' className='w-100 text-start text-gray-700 text-sm py-8 px-16 flex-align gap-8 hover-bg-gray-100 bg-transparent border-0' onClick={handleLogout}>
                          <i className='ph ph-sign-out' /> Logout
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <Link to='/login' className='text-white text-sm py-8 flex-align gap-6'>
                    <span className='icon text-md d-flex'><i className='ph ph-user-circle' /></span>
                    <span className='hover-text-decoration-underline'>My Account</span>
                  </Link>
                )}
              </li>
            </ul>
          </div>
        </div>
      </div>
      {/* ======================= Middle Top End ========================= */}
      {/* ======================= Middle Header Two Start ========================= */}
      <header className={headerClassName}>
        <div className='container container-lg'>
          <nav className='header-inner flex-between'>
            {/* Logo Start */}
            <div className='logo rghtnow-desktop-logo'>
              <Link to='/home' className='link'>
                <img src={`${process.env.PUBLIC_URL}/assets/images/logo/rghtnow-transparant-logo.png`} alt='RGHT NOW' />
              </Link>
            </div>
            {/* Logo End  */}
            {mobileHomeHeader && (
              <div className='rghtnow-mobile-appbar d-lg-none'>
                <button
                  type='button'
                  className='rghtnow-mobile-appbar__menu'
                  onClick={handleMenuToggle}
                  aria-label='Open menu'
                  aria-expanded={menuActive}
                  aria-controls='mobile-navigation'
                >
                  <i className='ph ph-list' />
                </button>
                <div className='rghtnow-mobile-appbar__title'>
                  <span>{mobileHeaderTitle}</span>
                  <Link
                    to='/address-list'
                    className='rghtnow-mobile-appbar__address'
                    onClick={handleAddressClick}
                  >
                    {mobileLocationText}
                  </Link>
                </div>
                <div className='rghtnow-mobile-appbar__actions'>
                  <Link
                    to='/my-account'
                    className='rghtnow-mobile-appbar__icon'
                    onClick={handleProfileClick}
                    aria-label='My account'
                  >
                    <i className='ph ph-user-circle' />
                  </Link>
                  <Link
                    to='/orders'
                    className='rghtnow-mobile-appbar__icon'
                    onClick={handleOrdersClick}
                    aria-label='Notifications'
                  >
                    <i className='ph ph-bell-simple' />
                  </Link>
                  <Link
                    to='/cart'
                    className='rghtnow-mobile-appbar__icon rghtnow-mobile-appbar__cart'
                    onClick={handleBasketClick}
                    aria-busy={basketLoading || cartUpdating}
                    aria-label='My basket'
                    title={basketTitle}
                  >
                    <i className='ph ph-shopping-cart-simple' />
                    <BasketBadge count={basketCount} />
                  </Link>
                </div>
              </div>
            )}
            <div className='rghtnow-mobile-header-actions d-lg-none flex-align gap-8 ms-auto'>
              {!hideHeaderSearch && (
              <button
                type='button'
                className='rghtnow-mobile-header-action w-36 h-36 flex-center rounded-circle border border-white text-white bg-transparent'
                onClick={handleSearchToggle}
                aria-label='Search products'
              >
                <i className='ph ph-magnifying-glass' />
              </button>
              )}
              <Link
                to='/orders'
                className='rghtnow-mobile-header-action w-36 h-36 flex-center rounded-circle border border-white text-white bg-transparent'
                onClick={handleOrdersClick}
                aria-label='My orders'
              >
                <i className='ph ph-package' />
              </Link>
              <Link
                to='/cart'
                className='rghtnow-mobile-header-action position-relative w-36 h-36 flex-center rounded-circle border border-white text-white bg-transparent'
                onClick={handleBasketClick}
                aria-busy={basketLoading || cartUpdating}
                aria-label='My basket'
                title={basketTitle}
              >
                <i className='ph ph-shopping-cart-simple' />
                <BasketBadge count={basketCount} />
              </Link>
              <button
                type='button'
                className='rghtnow-mobile-header-action w-36 h-36 flex-center rounded-circle border border-white text-white bg-transparent'
                onClick={handleMenuToggle}
                aria-label='Open menu'
                aria-expanded={menuActive}
                aria-controls='mobile-navigation'
              >
                <i className='ph ph-list' />
              </button>
            </div>
            {/* form Category Start */}
            <div className='flex-align gap-16'>
              <div className='select-dropdown-for-home-two d-lg-none d-block'>
                {/* Dropdown Select Start */}
                <ul className='header-top__right style-two flex-align flex-wrap'>
                  <li className='on-hover-item border-right-item border-right-item-sm-space has-submenu arrow-white'>
                    {/* Display the selected language */}
                    <Link
                      to='#'
                      className='selected-text text-heading text-sm py-8'
                    >
                      {selectedLanguage}
                    </Link>
                    <ul className='selectable-text-list on-hover-dropdown common-dropdown common-dropdown--sm max-h-200 scroll-sm px-0 py-8'>
                      <li>
                        <Link
                          to='#'
                          className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                          onClick={() => handleLanguageChange("English")}
                        >
                          <img
                            src='assets/images/thumbs/flag1.png'
                            alt='English'
                            className='w-16 h-12 rounded-4 border border-gray-100'
                          />
                          English
                        </Link>
                      </li>
                      <li>
                        <Link
                          to='#'
                          className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                          onClick={() => handleLanguageChange("Japan")}
                        >
                          <img
                            src='assets/images/thumbs/flag2.png'
                            alt='Japan'
                            className='w-16 h-12 rounded-4 border border-gray-100'
                          />
                          Japan
                        </Link>
                      </li>
                      <li>
                        <Link
                          to='#'
                          className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                          onClick={() => handleLanguageChange("French")}
                        >
                          <img
                            src='assets/images/thumbs/flag3.png'
                            alt='French'
                            className='w-16 h-12 rounded-4 border border-gray-100'
                          />
                          French
                        </Link>
                      </li>
                      <li>
                        <Link
                          to='#'
                          className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                          onClick={() => handleLanguageChange("Germany")}
                        >
                          <img
                            src='assets/images/thumbs/flag4.png'
                            alt='Germany'
                            className='w-16 h-12 rounded-4 border border-gray-100'
                          />
                          Germany
                        </Link>
                      </li>
                      <li>
                        <Link
                          to='#'
                          className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                          onClick={() => handleLanguageChange("Bangladesh")}
                        >
                          <img
                            src='assets/images/thumbs/flag6.png'
                            alt='Bangladesh'
                            className='w-16 h-12 rounded-4 border border-gray-100'
                          />
                          Bangladesh
                        </Link>
                      </li>
                      <li>
                        <Link
                          to='#'
                          className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                          onClick={() => handleLanguageChange("South Korea")}
                        >
                          <img
                            src='assets/images/thumbs/flag5.png'
                            alt='South Korea'
                            className='w-16 h-12 rounded-4 border border-gray-100'
                          />
                          South Korea
                        </Link>
                      </li>
                    </ul>
                  </li>
                  <li className='on-hover-item border-right-item border-right-item-sm-space has-submenu arrow-white'>
                    {/* Display the selected currency */}
                    <Link
                      to='#'
                      className='selected-text text-heading text-sm py-8'
                    >
                      {selectedCurrency}
                    </Link>
                    <ul className='selectable-text-list on-hover-dropdown common-dropdown common-dropdown--sm max-h-200 scroll-sm px-0 py-8'>
                      <li>
                        <Link
                          to='#'
                          className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                          onClick={() => handleCurrencyChange("AED")}
                        >
                          <img
                            src='assets/images/thumbs/flag1.png'
                            alt='UAE market'
                            className='w-16 h-12 rounded-4 border border-gray-100'
                          />
                          AED
                        </Link>
                      </li>
                      <li>
                        <Link
                          to='#'
                          className='hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0'
                          onClick={() => handleCurrencyChange("INR")}
                        >
                          <img
                            src='assets/images/thumbs/flag2.png'
                            alt='India market'
                            className='w-16 h-12 rounded-4 border border-gray-100'
                          />
                          INR
                        </Link>
                      </li>
                    </ul>
                  </li>
                </ul>
                {/* Dropdown Select End */}
              </div>

            </div>
            {/* form Category start */}
            {/* Header Middle Right start */}
            <div className='header-right flex-align d-lg-block d-none'>

              <div className='header-two-activities flex-align flex-wrap gap-32'>
                 {!hideHeaderSearch && (
                 <form
                action='#'
                className='flex-align flex-wrap form-location-wrapper'
                onSubmit={handleSearchSubmit}
              >
                <div className='search-category style-two d-flex h-48 search-form d-sm-flex d-none'>
                 {/* <select
                    defaultValue={1}
                    className='js-example-basic-single border border-gray-200 border-end-0 rounded-0 border-0'
                    name='state'
                  >
                    <option value={1}>All Categories</option>
                    <option value={1}>Grocery</option>
                    <option value={1}>Breakfast &amp; Dairy</option>
                    <option value={1}>Vegetables</option>
                    <option value={1}>Milks and Dairies</option>
                    <option value={1}>Pet Foods &amp; Toy</option>
                    <option value={1}>Breads &amp; Bakery</option>
                    <option value={1}>Fresh Seafood</option>
                    <option value={1}>Fronzen Foods</option>
                    <option value={1}>Noodles &amp; Rice</option>
                    <option value={1}>Ice Cream</option>
                  </select>*/}
                  <div className='search-form__wrapper position-relative bg-color-dark-gray'>
                    <label className='visually-hidden' htmlFor='header-search-input'>
                      {searchPlaceholder}
                    </label>
                    <input
                      id='header-search-input'
                      type='text'
                      className='search-form__input common-input placeholder-white text-white py-13 ps-16 pe-18 rounded-0 border-0'
                      {...searchInputProps}
                    />
                  </div>
                  <button
                    type='submit'
                    className='bg-white flex-center text-xl text-black flex-shrink-0 w-48 hover-bg-white d-lg-flex d-none'
                    aria-label='Submit search'
                  >
                    <i className='ph ph-magnifying-glass' />
                  </button>
                </div>
              </form>
                 )}
                {!hideHeaderSearch && (
                <button
                  type='button'
                  className='flex-align search-icon d-lg-none d-flex gap-4 item-hover-two'
                  onClick={handleSearchToggle}
                  aria-label={searchPlaceholder}
                >
                  <span className='text-2xl text-white d-flex position-relative item-hover__text'>
                    <i className='ph ph-magnifying-glass' />
                  </span>
                </button>
                )}
                {/* <Link
                  to='/home'
                  className='flex-align flex-column gap-8 item-hover-three-two'
                >
                  <span className='text-2xl text-white d-flex position-relative me-6 mt-6 item-hover-three__text'>
                    <i className="ph ph-package" />
                  </span>
                  <span className="text-md text-white item-hover-three__text">
                    Market Place
                  </span>
                </Link> */}
                {SHOW_API_ENV_TOGGLE && (
                  <button
                    type='button'
                    className='rn-api-env-toggle'
                    onClick={handleApiEnvironmentChange}
                    aria-label={`Switch API environment to ${apiEnvironment === "dev" ? "Live" : "Dev"}`}
                  >
                    <span className={apiEnvironment === "live" ? "is-active" : ""}>Live</span>
                    <span className={apiEnvironment === "dev" ? "is-active" : ""}>Dev</span>
                  </button>
                )}
                <Link
                  to='/orders'
                  className='flex-align flex-column gap-4 item-hover-three-two'
                  onClick={handleOrdersClick}
                >
                  <span className='text-2xl text-white d-flex position-relative me-6 mt-6 item-hover-three__text'>
                    <i className="ph ph-package" />
                   {/* <span className='w-16 h-16 flex-center rounded-circle bg-main-two-600 text-white text-xs position-absolute top-n6 end-n4'>
                      2
                    </span>*/}
                  </span>
                  <span className="text-md text-white item-hover-three__text">
                    My Orders
                  </span>
                </Link>
                <Link
                  to='/cart'
                  className='flex-align flex-column gap-4 item-hover-three-two'
                  onClick={handleBasketClick}
                  aria-busy={basketLoading || cartUpdating}
                  aria-label="My Basket"
                  title={basketTitle}
                >
                  <span className='text-2xl text-white d-flex position-relative me-6 mt-6 item-hover-three__text'>
                    <i className="ph ph-shopping-cart-simple" />
                    <BasketBadge count={basketCount} />
                  </span>
                  <span className="text-md text-white item-hover-three__text">
                    My Basket
                  </span>
                </Link>
                {isLoggedIn ? (
                  <div ref={accountMenuRef} className='rn-desktop-user-control position-relative flex-align flex-column gap-8 item-hover-three-two'>
                    <button
                      type='button'
                      className='flex-align flex-column gap-8 item-hover-three-two bg-transparent border-0 p-0'
                      onClick={handleUserAccountClick}
                      aria-expanded={accountMenuOpen}
                      aria-label='Open user menu'
                    >
                      <span className='text-2xl text-white d-flex position-relative me-6 mt-6 item-hover-three__text'>
                        <i className='ph ph-user-circle' />
                      </span>
                      <span className='text-md text-white item-hover-three__text d-none d-lg-flex'>
                        User
                      </span>
                    </button>
                    {accountMenuOpen && (
                      <div className='bg-white shadow-sm border border-gray-100 rounded-8 py-8 position-absolute end-0' style={{ minWidth: "230px", top: "calc(100% + 10px)", zIndex: 99 }}>
                        <div className='border-bottom border-gray-100 px-16 py-8 mb-4'>
                          <div className='text-gray-900 text-sm fw-semibold'>My Account</div>
                        </div>
                        <div className='rn-header-theme-switcher border-bottom border-gray-100 px-12 py-8 mb-4'>
                          <div className='rn-header-theme-switcher__label'>Header Color</div>
                          <div className='rn-header-theme-switcher__options'>
                            {headerThemeOptions.map((option) => (
                              <button type='button' key={option.value} className={`rn-header-theme-option ${headerTheme === option.value ? "is-active" : ""}`} onClick={() => handleHeaderThemeChange(option.value)}>
                                <span className={`rn-header-theme-swatch ${option.swatchClassName}`} />
                                <span>{option.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                        <Link to='/my-account' className='text-gray-700 text-sm py-8 px-16 flex-align gap-8 hover-bg-gray-100' onClick={() => setAccountMenuOpen(false)}>
                          <i className='ph ph-user' /> Profile
                        </Link>
                        <button type='button' className='w-100 text-start text-gray-700 text-sm py-8 px-16 flex-align gap-8 hover-bg-gray-100 bg-transparent border-0' onClick={handleLogout}>
                          <i className='ph ph-sign-out' /> Logout
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to='/login'
                    className='flex-align flex-column gap-8 item-hover-three-two'
                  >
                    <span className='text-2xl text-white d-flex position-relative me-6 mt-6 item-hover-three__text'>
                      <i className="ph ph-sign-in" />
                      {/*<span className='w-16 h-16 flex-center rounded-circle bg-main-two-600 text-white text-xs position-absolute top-n6 end-n4'>
                        2
                      </span>*/}
                    </span>
                    <span className='text-md text-white item-hover-three__text d-none d-lg-flex'>
                      Login
                    </span>
                  </Link>
                )}
              </div>
            </div>
            {/* Header Middle Right End  */}
          </nav>
        </div>
      </header>
      {/* ======================= Middle Header Two End ========================= */}
      <div className={`rghtnow-header-spacer ${scroll ? "is-active" : ""}`} aria-hidden='true' />
      {emptyCartOpen && (
        <div
          className='position-fixed top-0 start-0 w-100 h-100'
          style={{ background: "rgba(0, 0, 0, 0.45)", zIndex: 9999 }}
          onClick={() => setEmptyCartOpen(false)}
        >
          <div
            className='bg-white h-100 ms-auto shadow-lg p-24 d-flex flex-column'
            style={{ width: "min(420px, 100%)" }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className='d-flex align-items-center justify-content-between gap-16 pb-16 border-bottom border-gray-100'>
              <h5 className='mb-0'>My basket</h5>
              <button
                type='button'
                className='w-40 h-40 flex-center rounded-circle border border-gray-100 bg-white text-xl'
                onClick={() => setEmptyCartOpen(false)}
                aria-label='Close empty cart'
              >
                <i className='ph ph-x' />
              </button>
            </div>
            <div className='flex-grow-1 d-flex flex-column align-items-center justify-content-center text-center px-16'>
              <span className='w-72 h-72 rounded-circle bg-gray-50 flex-center text-4xl text-gray-500 mb-16'>
                <i className='ph ph-shopping-cart-simple' />
              </span>
              <h6 className='mb-8'>Cart empty</h6>
              <p className='text-gray-500 mb-0'>
                No cart has been created yet. Add a product and your basket will be ready.
              </p>
            </div>
          </div>
        </div>
      )}
      <GuestLoginRequiredModal
        show={guestLoginModalOpen}
        onClose={() => setGuestLoginModalOpen(false)}
        onContinue={handleGuestLoginContinue}
        message="You are browsing as a guest. Please login to access your profile, basket, and orders."
      />
    </>
  );
};

export default Header;
